/**
 * Service Registry - Central Registration System
 * 
 * This module handles the registration of all services in the application,
 * providing a centralized way to configure dependency injection.
 */

import { ServiceContainer, SERVICE_KEYS } from './service-container';
import { 
  HttpService, 
  createHttpService,
  getAuthTokenFromStore,
  getStoreFromContainer,
  getRouterFromContainer
} from './http-service';

// Import all data service classes
import { AthleteData } from '../../athlete/athlete-data';
import { AthleteDataService } from '../../athlete/athlete-data-service';
import { CompetitionData } from '../../competition/competition-data';
import { ClubData } from '../../club/club-data';
import { ConfigData } from '../../config/config-data';
import { BuilderData } from '../../builder/builder-data';
import { EventData } from '../../event/event-data';
import { LocationData } from '../../location/location-data';
import { SecondaryData } from '../../secondary/secondary-data';
import { SecondaryCustData } from '../../secondary/cust/secondary-cust-data';
import { SecondaryCustService } from '../../secondary/cust/secondary-cust-service';
import { CheckinData } from '../../competition/checkin/checkin-data';
import { FieldHelpData } from '../../common/ui/field/field-help/field-help-data';
import { FieldHelpService } from '../../common/ui/field/field-help/field-help-service';
import { AthleteCompSchedData } from '../../athleteCompSched/athletecompsched-data';
import { CompEventTeamHeaderData } from '../../athleteCompSched/comp-event-teams/comp-event-team-header-data';
import { UserProfileData } from '../../admin/user/user-data';
import { LoginData } from '../../auth/login-data';
import { TeamData } from '../../team/team-data';
import { PermissionData } from '../../admin/permissions/permission-data';
import { PermissionsService } from '../../admin/permissions/permissions-service';

// Import all business service classes
import { AthleteService } from '../../athlete/athlete-service';
import { CompetitionService } from '../../competition/competiton-service';
import { ConfigService } from '../../config/config-service';
import { AthleteCompSchedService } from '../../athleteCompSched/athletecompsched-service';
import { SecondaryService } from '../../secondary/secondary-service';
import { CommonService } from '../../common/common-service';
import { TeamService } from '../../team/team-service';
import { BuilderService } from '../../builder/builder-service';
import { BuilderStoreService } from '../../builder/builderStoreService';
import { AgeGroupService } from '../../agegroup/agegroup-service';
import { CompRuleService } from '../../comprule/comprule-service';

/**
 * Register all HTTP services
 */
function registerHttpServices(container: ServiceContainer): void {
  // Register HTTP service with dependency injection
  container.register(SERVICE_KEYS.HTTP_SERVICE, () => {
    const httpService = createHttpService();
    
    // Configure dependency injection
    httpService.setAuthTokenProvider(getAuthTokenFromStore);
    httpService.setStoreProvider(getStoreFromContainer);
    httpService.setRouterProvider(getRouterFromContainer);
    
    return httpService;
  });
}

/**
 * Register all data services
 */
function registerDataServices(container: ServiceContainer): void {
  // Athlete data services
  container.register(SERVICE_KEYS.ATHLETE_DATA, () => new AthleteData());
  container.register(SERVICE_KEYS.ATHLETE_DATA_SERVICE, () => new AthleteDataService());
  
  // Competition data services
  container.register(SERVICE_KEYS.COMPETITION_DATA, () => new CompetitionData());
  
  // Club data services
  container.register(SERVICE_KEYS.CLUB_DATA, () => new ClubData());
  
  // Config data services
  container.register(SERVICE_KEYS.CONFIG_DATA, () => new ConfigData());
  
  // Builder data services
  container.register(SERVICE_KEYS.BUILDER_DATA, () => new BuilderData());
  
  // Event data services
  container.register(SERVICE_KEYS.EVENT_DATA, () => new EventData());
  
  // Location data services
  container.register(SERVICE_KEYS.LOCATION_DATA, () => new LocationData());
  
  // Secondary data services
  container.register(SERVICE_KEYS.SECONDARY_DATA, () => new SecondaryData());
  container.register(SERVICE_KEYS.SECONDARY_CUST_DATA, () => new SecondaryCustData());
  
  // Checkin data services
  container.register(SERVICE_KEYS.CHECKIN_DATA, () => new CheckinData());
  
  // Field help data services
  container.register(SERVICE_KEYS.FIELD_HELP_DATA, () => new FieldHelpData());

  // Field help services
  container.register(SERVICE_KEYS.FIELD_HELP_SERVICE, () => new FieldHelpService());

  // Athlete comp sched data services
  container.register(SERVICE_KEYS.ATHLETE_COMP_SCHED_DATA, () => new AthleteCompSchedData());
  container.register(SERVICE_KEYS.COMP_EVENT_TEAM_HEADER_DATA, () => new CompEventTeamHeaderData());

  // User profile data services
  container.register(SERVICE_KEYS.USER_PROFILE_DATA, () => new UserProfileData());

  // Auth data services
  container.register(SERVICE_KEYS.LOGIN_DATA, () => new LoginData());

  // Team data services
  container.register(SERVICE_KEYS.TEAM_DATA, () => new TeamData());

  // Permission data services
  container.register(SERVICE_KEYS.PERMISSION_DATA, () => new PermissionData());
}

/**
 * Register all business services
 */
function registerBusinessServices(container: ServiceContainer): void {
  // Athlete services
  container.register(SERVICE_KEYS.ATHLETE_SERVICE, () => new AthleteService());
  
  // Competition services
  container.register(SERVICE_KEYS.COMPETITION_SERVICE, () => new CompetitionService());
  
  // Config services
  container.register(SERVICE_KEYS.CONFIG_SERVICE, () => new ConfigService());
  
  // Athlete comp sched services
  container.register(SERVICE_KEYS.ATHLETE_COMP_SCHED_SERVICE, () => new AthleteCompSchedService());
  
  // Secondary services
  container.register(SERVICE_KEYS.SECONDARY_SERVICE, () => new SecondaryService());
  container.register(SERVICE_KEYS.SECONDARY_CUST_SERVICE, () => new SecondaryCustService());

  // Common services
  container.register(SERVICE_KEYS.COMMON_SERVICE, () => new CommonService());

  // Team services
  container.register(SERVICE_KEYS.TEAM_SERVICE, () => new TeamService());

  // Builder services
  container.register(SERVICE_KEYS.BUILDER_SERVICE, () => new BuilderService());
  container.register(SERVICE_KEYS.BUILDER_STORE_SERVICE, () => new BuilderStoreService());
  container.register(SERVICE_KEYS.AGE_GROUP_SERVICE, () => new AgeGroupService());
  container.register(SERVICE_KEYS.COMP_RULE_SERVICE, () => new CompRuleService());

  // Permission services
  container.register(SERVICE_KEYS.PERMISSIONS_SERVICE, () => new PermissionsService());
}

/**
 * Initialize all services in the service container
 * This should be called once during application startup
 */
export function initializeServices(): void {
  const container = ServiceContainer.getInstance();
  
  console.log('[ServiceRegistry] Initializing service container...');
  
  // Register services in dependency order
  registerHttpServices(container);
  registerBusinessServices(container);
  registerDataServices(container);
  
  console.log('[ServiceRegistry] Service registration complete. Registered services:', 
    container.getRegisteredServices());
}

/**
 * Configure runtime context for the service container
 * This should be called after store and router are created
 */
export function configureRuntimeContext(store: any, router: any): void {
  const container = ServiceContainer.getInstance();
  container.setRuntimeContext(store, router);
  
  console.log('[ServiceRegistry] Runtime context configured');
}

/**
 * Get a service from the container with type safety
 */
export function getRegisteredService<T>(key: string): T {
  return ServiceContainer.getInstance().get<T>(key);
}

/**
 * Convenience functions for commonly used services
 */
export function getHttpService(): HttpService {
  return getRegisteredService<HttpService>(SERVICE_KEYS.HTTP_SERVICE);
}

export function getAthleteData(): AthleteData {
  return getRegisteredService<AthleteData>(SERVICE_KEYS.ATHLETE_DATA);
}

export function getAthleteDataService(): AthleteDataService {
  return getRegisteredService<AthleteDataService>(SERVICE_KEYS.ATHLETE_DATA_SERVICE);
}

export function getCompetitionData(): CompetitionData {
  return getRegisteredService<CompetitionData>(SERVICE_KEYS.COMPETITION_DATA);
}

export function getClubData(): ClubData {
  return getRegisteredService<ClubData>(SERVICE_KEYS.CLUB_DATA);
}

export function getConfigData(): ConfigData {
  return getRegisteredService<ConfigData>(SERVICE_KEYS.CONFIG_DATA);
}

export function getAthleteService(): AthleteService {
  return getRegisteredService<AthleteService>(SERVICE_KEYS.ATHLETE_SERVICE);
}

export function getCompetitionService(): CompetitionService {
  return getRegisteredService<CompetitionService>(SERVICE_KEYS.COMPETITION_SERVICE);
}

export function getConfigService(): ConfigService {
  return getRegisteredService<ConfigService>(SERVICE_KEYS.CONFIG_SERVICE);
}

export function getAthleteCompSchedService(): AthleteCompSchedService {
  return getRegisteredService<AthleteCompSchedService>(SERVICE_KEYS.ATHLETE_COMP_SCHED_SERVICE);
}

export function getAthleteCompSchedData(): AthleteCompSchedData {
  return getRegisteredService<AthleteCompSchedData>(SERVICE_KEYS.ATHLETE_COMP_SCHED_DATA);
}

export function getSecondaryService(): SecondaryService {
  return getRegisteredService<SecondaryService>(SERVICE_KEYS.SECONDARY_SERVICE);
}

export function getSecondaryCustService(): SecondaryCustService {
  return getRegisteredService<SecondaryCustService>(SERVICE_KEYS.SECONDARY_CUST_SERVICE);
}

export function getCommonService(): CommonService {
  return getRegisteredService<CommonService>(SERVICE_KEYS.COMMON_SERVICE);
}

export function getUserProfileData(): UserProfileData {
  return getRegisteredService<UserProfileData>(SERVICE_KEYS.USER_PROFILE_DATA);
}

export function getLoginData(): LoginData {
  return getRegisteredService<LoginData>(SERVICE_KEYS.LOGIN_DATA);
}

export function getTeamData(): TeamData {
  return getRegisteredService<TeamData>(SERVICE_KEYS.TEAM_DATA);
}

export function getTeamService(): TeamService {
  return getRegisteredService<TeamService>(SERVICE_KEYS.TEAM_SERVICE);
}

export function getBuilderService(): BuilderService {
  return getRegisteredService<BuilderService>(SERVICE_KEYS.BUILDER_SERVICE);
}

export function getBuilderStoreService(): BuilderStoreService {
  return getRegisteredService<BuilderStoreService>(SERVICE_KEYS.BUILDER_STORE_SERVICE);
}

export function getAgeGroupService(): AgeGroupService {
  return getRegisteredService<AgeGroupService>(SERVICE_KEYS.AGE_GROUP_SERVICE);
}

export function getCompRuleService(): CompRuleService {
  return getRegisteredService<CompRuleService>(SERVICE_KEYS.COMP_RULE_SERVICE);
}

export function getCheckinData(): CheckinData {
  return getRegisteredService<CheckinData>(SERVICE_KEYS.CHECKIN_DATA);
}

export function getFieldHelpData(): FieldHelpData {
  return getRegisteredService<FieldHelpData>(SERVICE_KEYS.FIELD_HELP_DATA);
}

export function getFieldHelpService(): FieldHelpService {
  return getRegisteredService<FieldHelpService>(SERVICE_KEYS.FIELD_HELP_SERVICE);
}

export function getCompEventTeamHeaderData(): CompEventTeamHeaderData {
  return getRegisteredService<CompEventTeamHeaderData>(SERVICE_KEYS.COMP_EVENT_TEAM_HEADER_DATA);
}

export function getPermissionData(): PermissionData {
  return getRegisteredService<PermissionData>(SERVICE_KEYS.PERMISSION_DATA);
}

export function getPermissionsService(): PermissionsService {
  return getRegisteredService<PermissionsService>(SERVICE_KEYS.PERMISSIONS_SERVICE);
}

/**
 * Debug function to list all registered services
 */
export function debugServices(): void {
  const container = ServiceContainer.getInstance();
  const services = container.getRegisteredServices();
  
  console.log('[ServiceRegistry] Registered services:');
  services.forEach(key => {
    const info = container.getServiceInfo(key);
    console.log(`  - ${key}: singleton=${info?.singleton}, instantiated=${!!info?.instance}`);
  });
}